import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { api, fileUrl } from "@/lib/api";
import { ArrowUpRight, MapPin, Calendar } from "lucide-react";
import Seo from "@/components/Seo";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Projects() {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api
      .get("/updates")
      .then((r) => setUpdates(r.data || []))
      .catch(() => setUpdates([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = ["All", ...Array.from(new Set(updates.map((u) => u.category)))];
  const filtered = filter === "All" ? updates : updates.filter((u) => u.category === filter);

  return (
    <div data-testid="page-projects">
      <Seo
        path="/projects"
        title="Projects & Daily Updates"
        description="Live field log of VOLTECH POWER SOLUTIONS — completed lithium battery deliveries, solar street light installations, EV builds, CSR drives and on-ground stories."
      />
      <PageHeader
        eyebrow="Field log"
        title="Projects & daily updates."
        subtitle="A live feed of completed installations, custom builds, CSR drives and field stories from the VPS team."
        testId="projects-page-header"
      />

      <section className="py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-center gap-2" data-testid="projects-filter-bar">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`projects-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] border transition-colors ${
                  filter === c
                    ? "border-[#e63946] text-[#e63946] bg-[#e63946]/5"
                    : "border-white/15 text-zinc-400 hover:text-white hover:border-white/40"
                }`}
              >
                {c}
              </button>
            ))}
            <div className="ml-auto font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
              {filtered.length} entries
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="industrial-card h-96 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20" data-testid="projects-empty">
              <div className="eyebrow">No updates yet</div>
              <p className="text-zinc-400 mt-3">
                Check back soon — new projects are posted regularly.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((u, idx) => (
                <Link
                  key={u.id}
                  to={`/projects/${u.id}`}
                  className="industrial-card group overflow-hidden flex flex-col"
                  data-testid={`project-card-${idx}`}
                >
                  {u.image_url && (
                    <div className="aspect-[16/10] overflow-hidden border-b border-white/10">
                      <img
                        src={fileUrl(u.image_url)}
                        alt={u.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="tag-chip tag-chip-red">{u.category}</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
                        <Calendar size={10} /> {formatDate(u.created_at)}
                      </span>
                    </div>
                    <h3 className="font-display text-lg uppercase mt-4 leading-tight">
                      {u.title}
                    </h3>
                    <p className="text-sm text-zinc-400 mt-3 leading-relaxed flex-1">
                      {u.summary}
                    </p>
                    <div className="mt-4 flex items-center justify-between text-zinc-500">
                      {u.location && (
                        <span className="text-xs flex items-center gap-1">
                          <MapPin size={12} /> {u.location}
                        </span>
                      )}
                      <ArrowUpRight
                        size={16}
                        className="ml-auto group-hover:text-[#e63946] transition-colors"
                      />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
